/**
 * useDownloadContribution (P168) -- fetch the signed, anonymized
 * benchmark contribution bundle (JSON) + trigger a browser download.
 * This bundle is the 5b federation handshake. Manual bearer (mirrors
 * useDownloadAnswerPack).
 */
import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { currentAuthToken } from "shared/api/client";

export class ContributionDownloadError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
  ) {
    super(detail);
    this.name = "ContributionDownloadError";
  }
}

async function _download(): Promise<{ filename: string }> {
  const token = currentAuthToken();
  const response = await fetch("/v1/benchmark/contribution", {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    let detail = response.statusText || `HTTP ${response.status}`;
    try {
      detail = (await response.json())?.detail ?? detail;
    } catch {
      // keep statusText
    }
    throw new ContributionDownloadError(response.status, detail);
  }
  const text = await response.text();
  const blob = new Blob([text], { type: "application/json" });
  const filename = "marsad-benchmark-contribution.json";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
  return { filename };
}

export function useDownloadContribution(): UseMutationResult<
  { filename: string },
  ContributionDownloadError,
  void
> {
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: _download,
  });
}
